import React, { useState, ChangeEvent, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CardMedia, Input, TextField, Button, MenuItem, Select, SelectChangeEvent, CircularProgress } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useGetRolesListQuery, useGetStoreListQuery, useCreateSubAdminMutation, useGetSubAdminDetailQuery, useUpdateSubAdminMutation } from "../../store/api/manageAPI";
import { useUploadFileMutation } from "../../store/api/commonAPI";
import { USER_TYPES } from "../../constants/enums";
import { showError, showToast } from "../../constants/toasts";

const AddSubadmin = () => {

  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [image, setImage] = useState<string>("");
  const [uploadedUrl, setUploadedUrl] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [password, setPassword] = useState("");

  const [selectedRole, setSelectedRole] = useState('default');

  const { data: rolesData } = useGetRolesListQuery({
    page: 1,
    limit: 100
  });

  const [createSubAdmin, { isLoading: isCreating }] = useCreateSubAdminMutation();
  const [updateSubAdmin, { isLoading: isUpdating }] = useUpdateSubAdminMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

  const { data: subAdminData, isLoading: isFetching } = useGetSubAdminDetailQuery({ id: id || "" }, { skip: !isEditMode });

  useEffect(() => {
    if (isEditMode && subAdminData) {
      // Handle potential nesting or direct data
      const rawData: any = subAdminData;
      const data = rawData.data || rawData;

      if (data) {
        setName(data.fullName || (data.firstName ? data.firstName + " " + (data.lastName || "") : "") || "");
        setEmail(data.email || "");

        // Phone handling
        const phoneStr = data.phone ? String(data.phone) : "";
        const countryCodeStr = data.countryCode ? String(data.countryCode).replace("+", "") : "91";

        // Construct phone for display (react-phone-input expects full number)
        if (phoneStr && countryCodeStr) {
          if (phoneStr.startsWith(countryCodeStr)) {
            setPhone(phoneStr);
          } else {
            setPhone(countryCodeStr + phoneStr);
          }
        }
        setCountryCode(countryCodeStr);

        setImage(data.image || "");
        setUploadedUrl(data.image || "");

        // Handle Access Role (Custom Role ID)
        let customRoleId = 'default';
        if (data.accessRole) {
          if (typeof data.accessRole === 'object' && data.accessRole._id) {
            customRoleId = data.accessRole._id;
          } else if (typeof data.accessRole === 'string') {
            customRoleId = data.accessRole;
          }
        }
        setSelectedRole(customRoleId);

        // Handle System Role
        setAccessRole(data.role || 'default');

        if (data.role === USER_TYPES.STORE_MANAGER && data.storeId) {
          setSelectedStore(data.storeId);
        }
      }
    }
  }, [isEditMode, subAdminData]);

  const handleChangeRole = (event: SelectChangeEvent) => {
    setSelectedRole(event.target.value as string);
  };

  const [accessRole, setAccessRole] = useState<string | number>('default');
  const [selectedStore, setSelectedStore] = useState('default');

  const { data: storeData } = useGetStoreListQuery({
    page: 1,
    limit: 100
  }, {
    skip: accessRole !== USER_TYPES.STORE_MANAGER
  });

  const handleChangeAccessRole = (event: SelectChangeEvent<string | number>) => {
    setAccessRole(event.target.value);
  };

  const handleChangeStore = (event: SelectChangeEvent) => {
    setSelectedStore(event.target.value as string);
  };

  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]) {
      const file = files[0];
      if (!file.type.includes("image")) {
        showError("Please add image only.");
        return;
      }
      setFileName(file.name);
      setImage(URL.createObjectURL(file));

      try {
        const response = await uploadFile({ file }).unwrap();
        const url = response?.data?.url || response?.data?.fileUrl || response?.data?.data || response?.data;

        if (typeof url === 'string') {
          setUploadedUrl(url);
        } else {
          showError("Failed to get image URL");
        }
      } catch (error) {
        showError("Image upload failed");
      }
    }
  };

  const handleSave = async () => {
    if (!uploadedUrl) {
      showError("Please upload an image and wait for it to finish uploading.");
      return;
    }
    if (!name.trim()) {
      showError("Please enter name");
      return;
    }
    if (name.length > 40) {
      showError("Name should not exceed 40 characters");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      showError("Please enter email id");
      return;
    }
    if (!emailRegex.test(email)) {
      showError("Please enter a valid email id");
      return;
    }
    if (!phone) {
      showError("Please enter phone number");
      return;
    }
    if (selectedRole === 'default') {
      showError("Please select a role");
      return;
    }
    if (accessRole === 'default') {
      showError("Please select an access role");
      return;
    }
    if (accessRole === USER_TYPES.STORE_MANAGER && selectedStore === 'default') {
      showError("Please select a store");
      return;
    }
    // Password Validation - Only required for Create mode
    if (!isEditMode) {
      if (!password || !password.trim()) {
        showError("Please enter password");
        return;
      }
      if (password.length < 8 || password.length > 15) {
        showError("Password must be between 8 and 15 characters");
        return;
      }
      if (!/[A-Z]/.test(password)) {
        showError("Password must contain at least one uppercase letter");
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        showError("Password must contain at least one special character");
        return;
      }
    }

    try {
      // Prepare Payload
      let cleanPhone = phone;
      if (countryCode && phone.startsWith(countryCode)) {
        cleanPhone = phone.slice(countryCode.length);
      }

      const payload: any = {
        fullName: name.trim(),
        email: email,
        phone: cleanPhone,
        countryCode: countryCode ? `+${countryCode}` : "+91",
        image: uploadedUrl,
        role: accessRole,
        accessRole: selectedRole,
      };

      if (password) {
        payload.password = password;
      }

      if (accessRole === USER_TYPES.STORE_MANAGER) {
        payload.storeId = selectedStore;
      }

      let response;
      if (isEditMode && id) {
        response = await updateSubAdmin({ id, data: payload }).unwrap();
      } else {
        response = await createSubAdmin(payload).unwrap();
      }

      if (response && (response.success || response.status === 'success' || response.message === 'Added successfully' || response.message === 'Updated successfully')) {
        showToast(response.message || `Sub-Admin ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate("/manage-subadmin");
      } else {
        showError(response.message || `Failed to ${isEditMode ? 'update' : 'create'} Sub-Admin`);
      }

    } catch (error: any) {
      showError(error?.data?.message || "Something went wrong");
    }
  };

  if (isFetching) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>{isEditMode ? "Edit Sub-Admin" : "Add Sub-Admin"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {' '} - <button onClick={() => navigate("/manage-subadmin")}>Manage Sub-Admin</button>
          {' '} - {isEditMode ? "Edit Sub-Admin" : "Add Sub-Admin"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_100">
              <label htmlFor="icon-button-file">Upload Image</label>
              {isUploading && <div style={{ marginBottom: "10px", color: "#666" }}>Uploading image...</div>}
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setUploadedUrl("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={handleImageChange}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/user_placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            <div className="control_group w_33">
              <label htmlFor="name">Name</label>
              <TextField
                id="name"
                placeholder="Enter Name"
                hiddenLabel
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="email">Email Id</label>
              <TextField
                id="email"
                placeholder="Enter Email Id"
                hiddenLabel
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ maxLength: 60 }}
                disabled={isEditMode} // Usually email is not editable
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="phone">Phone Number</label>
              <PhoneInput
                inputProps={{ id: "phone" }}
                country={"in"}
                placeholder="Enter Phone Number"
                enableSearch={false}
                autoFormat={false}
                inputStyle={{ width: "100%" }}
                value={phone}
                onChange={(phone, data: any) => {
                  setPhone(phone);
                  setCountryCode(data.dialCode);
                }}
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="role">Role</label>
              <Select
                id="role"
                fullWidth
                value={selectedRole}
                onChange={handleChangeRole}
                displayEmpty
              >
                <MenuItem disabled value="default">
                  Select Role
                </MenuItem>
                {rolesData?.data?.list?.map((role: any) => (
                  <MenuItem key={role._id} value={role._id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label htmlFor="accessRole">Access Role</label>
              <Select
                id="accessRole"
                fullWidth
                value={accessRole}
                onChange={handleChangeAccessRole}
                displayEmpty
              >
                <MenuItem disabled value="default">Select Access Role</MenuItem>
                <MenuItem value={USER_TYPES.SUBADMIN}>Sub-Admin</MenuItem>
                <MenuItem value={USER_TYPES.STORE_MANAGER}>Store Manager</MenuItem>
              </Select>
            </div>

            {accessRole === USER_TYPES.STORE_MANAGER && (
              <div className="control_group w_33">
                <label htmlFor="store">Store</label>
                <Select
                  id="store"
                  fullWidth
                  value={selectedStore}
                  onChange={handleChangeStore}
                  displayEmpty
                >
                  <MenuItem disabled value="default">
                    Select Store
                  </MenuItem>
                  {storeData?.data?.store?.map((store: any) => (
                    <MenuItem key={store._id} value={store._id}>
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}

            <div className="control_group w_33">
              <label htmlFor="password">Password</label>
              <TextField
                id="password"
                placeholder="Enter Password"
                hiddenLabel
                fullWidth
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                inputProps={{ maxLength: 15 }}
                helperText={isEditMode ? "Leave empty to keep unchanged" : ""}
              />
            </div>
          </div>

          <div className="form_btn">
            <Button className="btn btn_primary" onClick={handleSave} disabled={isCreating || isUploading || isUpdating}>Save</Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddSubadmin;
